import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import styled from 'styled-components';
import { 
  Table, 
  Input, 
  InputNumber,
  Select, 
  Slider, 
  Tag, 
  Space, 
  Button, 
  Card,
  Row,
  Col,
  Tooltip,
  ConfigProvider,
  message,
  Modal,
  Statistic,
  Badge,
  Typography,
  Tabs,
  Progress,
  Timeline,
  Divider,
  Avatar,
  List,
  Alert
} from 'antd';
import { 
  SearchOutlined, 
  FilterOutlined,
  DollarOutlined,
  UserOutlined,
  SafetyCertificateOutlined,
  TrophyOutlined,
  RiseOutlined,
  EnvironmentOutlined,
  PhoneOutlined,
  BuildOutlined,
  CloseOutlined,
  BarChartOutlined,
  FileTextOutlined,
  TeamOutlined,
  FundOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  LinkOutlined,
  PieChartOutlined,
  LineChartOutlined,
  InfoCircleOutlined,
  WarningOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  BankOutlined
} from '@ant-design/icons';
import { TrendingUp, Users, DollarSign, Target } from 'lucide-react';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip as RechartsTooltip, 
  ResponsiveContainer,
  Legend,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar
} from 'recharts';
import { useNavigate } from 'react-router-dom';

const { Option } = Select;
const { Title, Text, Paragraph } = Typography;

// Styled Components
const PageContainer = styled(motion.div)`
  padding: 24px;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  min-height: calc(100vh - 48px);
  height: calc(100vh - 48px);
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const GlassCard = styled(Card)`
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.18);
  box-shadow: 0 8px 32px 0 rgba(31, 38, 135, 0.15);
  border-radius: 16px;
  transition: all 0.3s ease;
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 48px 0 rgba(31, 38, 135, 0.2);
  }
  
  .ant-card-body {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    padding: 0;
  }
`;

const SearchSection = styled(GlassCard)`
  margin-bottom: 16px;
  padding: 0px;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 249, 252, 0.95) 100%);
  flex: none;
  
  .ant-card-body {
    padding: 16px;
    flex: none;
  }
`;

const FiltersSection = styled(motion.div)`
  margin-bottom: 16px;
  padding: 16px;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(10px);
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.05);
`;

const ContentWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 24px;
  
  @media (max-width: 767px) {
    padding: 16px;
  }
`;

const TableWrapper = styled.div`
  flex: 1;
  overflow: auto;
  border-radius: 12px;
`;

const StyledTable = styled(Table)`
  height: 100%;
  
  .ant-table-wrapper {
    height: 100%;
  }
  
  .ant-table {
    background: white;
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
  }
  
  .ant-table-thead > tr > th {
    background: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(8px);
    font-weight: 600;
    color: #1a202c;
    border-bottom: 2px solid #e2e8f0;
    position: sticky;
    top: 0;
    z-index: 10;
  }
  
  .ant-table-tbody > tr {
    transition: all 0.3s ease;
    
    &:hover {
      transform: translateX(4px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
      
      > td {
        background: rgba(255, 255, 255, 0.95) !important;
      }
    }
    
    > td {
      cursor: pointer;
      border-bottom: 1px solid #f0f0f0;
      background: white;
    }
  }
  
  .ant-table-pagination {
    background: white;
    padding: 16px;
    border-top: 1px solid #e2e8f0;
  }
`;

const StatsCard = styled(motion.div)`
  background: linear-gradient(135deg, ${props => props.gradient || '#667eea 0%, #764ba2 100%'});
  border-radius: 12px;
  padding: 20px;
  color: white;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const StatsSection = styled(motion.div)`
  margin-bottom: 16px;
`;

const FilterTag = styled(Tag)`
  border-radius: 20px;
  padding: 4px 12px;
  font-size: 13px;
  border: none;
  background: rgba(99, 102, 241, 0.1);
  color: #6366f1;
  
  .ant-tag-close-icon {
    color: #6366f1;
    margin-left: 4px;
  }
`;

const PLAN_CODES_MAP = {
  '2E': 'Participant-directed account plan',
  '2F': 'Total participant-directed account plan',
  '2J': 'Participant-directed account plan with partial participant direction',
  '3D': 'Self-employed plan',
  '2G': 'ESOP other than a leveraged ESOP',
  '2H': 'Leveraged ESOP',
  '2K': 'Other plan feature'
};

// Range Input Component
const RangeInputFilter = ({ label, range, onChange, min, max, step, formatter, parser, suffix = '' }) => {
  return (
    <>
      <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
        {label}
      </label>
      <Space.Compact style={{ width: '100%', marginBottom: 8 }}>
        <InputNumber
          min={min}
          max={max}
          step={step}
          value={range[0]}
          onChange={(value) => {
            if (value !== null && value <= range[1]) {
              onChange([value, range[1]]);
            }
          }}
          formatter={formatter}
          parser={parser}
          style={{ width: '50%' }}
          placeholder="Min"
        />
        <InputNumber
          min={min}
          max={max}
          step={step}
          value={range[1]}
          onChange={(value) => {
            if (value !== null && value >= range[0]) {
              onChange([range[0], value]);
            }
          }}
          formatter={formatter}
          parser={parser}
          style={{ width: '50%' }}
          placeholder="Max"
        />
      </Space.Compact>
      <Slider
        range
        min={min}
        max={max}
        step={step}
        value={range}
        onChange={onChange}
        tooltip={{
          formatter: formatter || ((value) => `${value}${suffix}`)
        }}
      />
    </>
  );
};

const PlanSearchAutomation = () => {
  // State Management
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const navigate = useNavigate();
  
  // Filter States
  const [filters, setFilters] = useState({
    state: [],
    city: [],
    ein: '',
    pn: '',
    assetRange: [0, 10000000],
    participantRange: [0, 500],
    estimatedGrowthRange: [0, 50],
    oddsRange: [0, 100],
    dppRange: [0, 200000],
    planCodes: [],
    planType: [],
    healthScore: [],
    wealthScore: [],
    industry: []
  });


  // Statistics
  const statistics = useMemo(() => {
    if (!filteredData.length) return null;
    
    const totalAssets = filteredData.reduce((sum, plan) => {
      const latestYear = Object.keys(plan.yearly_data).sort().pop();
      return sum + (plan.yearly_data[latestYear]?.assets || 0);
    }, 0);
    
    const totalParticipants = filteredData.reduce((sum, plan) => {
      const latestYear = Object.keys(plan.yearly_data).sort().pop();
      return sum + (plan.yearly_data[latestYear]?.participants_eoy || 0);
    }, 0);
    
    const avgGrowthRate = filteredData.reduce((sum, plan) => 
      sum + (plan.estimated_growth_rate || 0), 0) / filteredData.length;
    
    return {
      totalPlans: filteredData.length,
      totalAssets,
      totalParticipants,
      avgGrowthRate
    };
  }, [filteredData]);

  // Fetch Plans Data
  const fetchPlans = useCallback(async () => {
    setLoading(true);
    try {
      // TODO: Replace with actual API endpoint
      // const response = await api.get('/api/plans/search');
      // setData(response.data);
      
      // Using mock data for now
      const mockData = [
        {
          "ein": "590123456",
          "pn": "001",
          "plan_name": "Winter Park Dental 401(k) Plan",
          "sponsor_name": "Winter Park Dental Group, PLLC",
          "address": "301 S Park Ave Suite 100",
          "city": "Winter Park",
          "state": "FL",
          "zip": "32789",
          "phone_number": "407-555-0123",
          "plan_codes": "2E,3D",
          "plan_type": "Single-Employer plan",
          "plan_sponsor_industry": "Healthcare",
          "yearly_data": {
            "2018": { "assets": 190000.0, "assets_boy": 150000.0, "participants": 6, "participants_eoy": 7 },
            "2019": { "assets": 240000.0, "assets_boy": 190000.0, "participants": 7, "participants_eoy": 7 },
            "2020": { "assets": 295000.0, "assets_boy": 240000.0, "participants": 7, "participants_eoy": 8 },
            "2021": { "assets": 410000.0, "assets_boy": 295000.0, "participants": 8, "participants_eoy": 9 },
            "2022": { "assets": 520000.0, "assets_boy": 410000.0, "participants": 9, "participants_eoy": 10 },
            "2023": { "assets": 645000.0, "assets_boy": 520000.0, "participants": 10, "participants_eoy": 12 },
            "2024": { "assets": 760000.0, "assets_boy": 645000.0, "participants": 12, "participants_eoy": 13 },
            "2025": { "assets": 880000.0, "assets_boy": 760000.0, "participants": 13, "participants_eoy": 14 }
          },
          "compliance_flags_by_year": {
            "2018": 1,
            "2019": 0,
            "2020": 0,
            "2021": 0,
            "2022": 1,
            "2023": 0,
            "2024": 0,
            "2025": 0
          },
          "form_5500_links_by_year": {
            "2018": "https://efast.dol.gov/5500/winter-park/2018",
            "2019": "https://efast.dol.gov/5500/winter-park/2019",
            "2020": "https://efast.dol.gov/5500/winter-park/2020",
            "2021": "https://efast.dol.gov/5500/winter-park/2021",
            "2022": "https://efast.dol.gov/5500/winter-park/2022",
            "2023": "https://efast.dol.gov/5500/winter-park/2023",
            "2024": "https://efast.dol.gov/5500/winter-park/2024",
            "2025": "https://efast.dol.gov/5500/winter-park/2025"
          },
          "growth_rate_by_year": {
            "2019": 26.3,
            "2020": 22.9,
            "2021": 39.0,
            "2022": 26.8,
            "2023": 24.0,
            "2024": 17.8,
            "2025": 15.8
          },
          "estimated_growth_rate": 14.0,
          "odds_of_zero_assets": 0.5,
          "plan_health_score": "A",
          "individual_wealth_score": "High",
          "dollars_per_participant": {
            "2023": 53750.0,
            "2024": 58461.5,
            "2025": 62857.1
          }
        },
        {
          "ein": "560987654",
          "pn": "002",
          "plan_name": "Triangle Software Solutions 401(k) & Profit Sharing Plan",
          "sponsor_name": "Triangle Software Solutions, Inc.",
          "address": "555 Tech Park Dr",
          "city": "Cary",
          "state": "NC",
          "zip": "27513",
          "phone_number": "919-555-9876",
          "plan_codes": "2E,2F,2J,3D",
          "plan_type": "Single-Employer plan",
          "plan_sponsor_industry": "Technology",
          "yearly_data": {
            "2018": { "assets": 610000.0, "assets_boy": 500000.0, "participants": 24, "participants_eoy": 26 },
            "2019": { "assets": 850000.0, "assets_boy": 610000.0, "participants": 26, "participants_eoy": 30 },
            "2020": { "assets": 990000.0, "assets_boy": 850000.0, "participants": 30, "participants_eoy": 34 },
            "2021": { "assets": 1320000.0, "assets_boy": 990000.0, "participants": 34, "participants_eoy": 38 },
            "2022": { "assets": 1600000.0, "assets_boy": 1320000.0, "participants": 38, "participants_eoy": 40 },
            "2023": { "assets": 1880000.0, "assets_boy": 1600000.0, "participants": 40, "participants_eoy": 43 },
            "2024": { "assets": 2160000.0, "assets_boy": 1880000.0, "participants": 43, "participants_eoy": 46 },
            "2025": { "assets": 2450000.0, "assets_boy": 2160000.0, "participants": 46, "participants_eoy": 49 }
          },
          "compliance_flags_by_year": {
            "2018": 0,
            "2019": 0,
            "2020": 1,
            "2021": 0,
            "2022": 1,
            "2023": 0,
            "2024": 0,
            "2025": 0
          },
          "form_5500_links_by_year": {
            "2018": "https://efast.dol.gov/5500/triangle/2018",
            "2019": "https://efast.dol.gov/5500/triangle/2019",
            "2020": "https://efast.dol.gov/5500/triangle/2020",
            "2021": "https://efast.dol.gov/5500/triangle/2021",
            "2022": "https://efast.dol.gov/5500/triangle/2022",
            "2023": "https://efast.dol.gov/5500/triangle/2023",
            "2024": "https://efast.dol.gov/5500/triangle/2024",
            "2025": "https://efast.dol.gov/5500/triangle/2025"
          },
          "growth_rate_by_year": {
            "2019": 39.3,
            "2020": 16.5,
            "2021": 33.3,
            "2022": 21.2,
            "2023": 17.5,
            "2024": 14.9,
            "2025": 13.4
          },
          "estimated_growth_rate": 12.0,
          "odds_of_zero_assets": 0.1,
          "plan_health_score": "B+",
          "individual_wealth_score": "Medium",
          "dollars_per_participant": {
            "2023": 43720.9,
            "2024": 46956.5,
            "2025": 50000.0
          }
        },
        {
            "ein": "470112233",
            "pn": "003",
            "plan_name": "Nebraska Agri-Services 401(k) Plan",
            "sponsor_name": "Nebraska Agri-Services LLC",
            "address": "100 Cornfield Rd",
            "city": "Lincoln",
            "state": "NE",
            "zip": "68508",
            "phone_number": "402-555-1234",
            "plan_codes": "2E,3D",
            "plan_type": "Single-Employer plan",
            "plan_sponsor_industry": "Agriculture",
            "yearly_data": {
              "2018": { "assets": 120000.0, "assets_boy": 100000.0, "participants": 5, "participants_eoy": 6 },
              "2019": { "assets": 150000.0, "assets_boy": 120000.0, "participants": 6, "participants_eoy": 7 },
              "2020": { "assets": 180000.0, "assets_boy": 150000.0, "participants": 7, "participants_eoy": 8 },
              "2021": { "assets": 210000.0, "assets_boy": 180000.0, "participants": 8, "participants_eoy": 9 },
              "2022": { "assets": 250000.0, "assets_boy": 210000.0, "participants": 9, "participants_eoy": 10 },
              "2023": { "assets": 300000.0, "assets_boy": 250000.0, "participants": 10, "participants_eoy": 11 },
              "2024": { "assets": 350000.0, "assets_boy": 300000.0, "participants": 11, "participants_eoy": 12 },
              "2025": { "assets": 400000.0, "assets_boy": 350000.0, "participants": 12, "participants_eoy": 13 }
            },
            "compliance_flags_by_year": {
              "2018": 0,
              "2019": 0,
              "2020": 0,
              "2021": 1,
              "2022": 0,
              "2023": 0,
              "2024": 0,
              "2025": 0
            },
            "form_5500_links_by_year": {
              "2018": "https://efast.dol.gov/5500/nebraska/2018",
              "2019": "https://efast.dol.gov/5500/nebraska/2019",
              "2020": "https://efast.dol.gov/5500/nebraska/2020",
              "2021": "https://efast.dol.gov/5500/nebraska/2021",
              "2022": "https://efast.dol.gov/5500/nebraska/2022",
              "2023": "https://efast.dol.gov/5500/nebraska/2023",
              "2024": "https://efast.dol.gov/5500/nebraska/2024",
              "2025": "https://efast.dol.gov/5500/nebraska/2025"
            },
            "growth_rate_by_year": {
              "2019": 25.0,
              "2020": 20.0,
              "2021": 16.7,
              "2022": 19.0,
              "2023": 20.0,
              "2024": 16.7,
              "2025": 14.3
            },
            "estimated_growth_rate": 13.0,
            "odds_of_zero_assets": 0.2,
            "plan_health_score": "B",
            "individual_wealth_score": "Medium",
            "dollars_per_participant": {
              "2023": 27272.7,
              "2024": 29166.7,
              "2025": 30769.2
            }
          },
          {
            "ein": "130998877",
            "pn": "004",
            "plan_name": "Manhattan Law Group 401(k) Plan",
            "sponsor_name": "Manhattan Law Group LLP",
            "address": "200 Broadway Ave",
            "city": "New York",
            "state": "NY",
            "zip": "10007",
            "phone_number": "212-555-9988",
            "plan_codes": "2E,3D,2J",
            "plan_type": "Single-Employer plan",
            "plan_sponsor_industry": "Legal",
            "yearly_data": {
              "2018": { "assets": 900000.0, "assets_boy": 800000.0, "participants": 20, "participants_eoy": 22 },
              "2019": { "assets": 1050000.0, "assets_boy": 900000.0, "participants": 22, "participants_eoy": 24 },
              "2020": { "assets": 1200000.0, "assets_boy": 1050000.0, "participants": 24, "participants_eoy": 26 },
              "2021": { "assets": 1400000.0, "assets_boy": 1200000.0, "participants": 26, "participants_eoy": 28 },
              "2022": { "assets": 1600000.0, "assets_boy": 1400000.0, "participants": 28, "participants_eoy": 30 },
              "2023": { "assets": 1800000.0, "assets_boy": 1600000.0, "participants": 30, "participants_eoy": 32 },
              "2024": { "assets": 2000000.0, "assets_boy": 1800000.0, "participants": 32, "participants_eoy": 34 },
              "2025": { "assets": 2200000.0, "assets_boy": 2000000.0, "participants": 34, "participants_eoy": 36 }
            },
            "compliance_flags_by_year": {
              "2018": 0,
              "2019": 1,
              "2020": 0,
              "2021": 0,
              "2022": 0,
              "2023": 0,
              "2024": 0,
              "2025": 0
            },
            "form_5500_links_by_year": {
              "2018": "https://efast.dol.gov/5500/manhattan/2018",
              "2019": "https://efast.dol.gov/5500/manhattan/2019",
              "2020": "https://efast.dol.gov/5500/manhattan/2020",
              "2021": "https://efast.dol.gov/5500/manhattan/2021",
              "2022": "https://efast.dol.gov/5500/manhattan/2022",
              "2023": "https://efast.dol.gov/5500/manhattan/2023",
              "2024": "https://efast.dol.gov/5500/manhattan/2024",
              "2025": "https://efast.dol.gov/5500/manhattan/2025"
            },
            "growth_rate_by_year": {
              "2019": 16.7,
              "2020": 14.3,
              "2021": 16.7,
              "2022": 14.3,
              "2023": 12.5,
              "2024": 11.1,
              "2025": 10.0
            },
            "estimated_growth_rate": 11.0,
            "odds_of_zero_assets": 0.05,
            "plan_health_score": "A",
            "individual_wealth_score": "High",
            "dollars_per_participant": {
              "2023": 56250.0,
              "2024": 62500.0,
              "2025": 61111.1
            }
          },
          {
            "ein": "220334455",
            "pn": "005",
            "plan_name": "Garden State Manufacturing 401(k) Plan",
            "sponsor_name": "Garden State Manufacturing Inc.",
            "address": "400 Industrial Pkwy",
            "city": "Edison",
            "state": "NJ",
            "zip": "08817",
            "phone_number": "732-555-3344",
            "plan_codes": "2E,3D,2F",
            "plan_type": "Single-Employer plan",
            "plan_sponsor_industry": "Manufacturing",
            "yearly_data": {
              "2018": { "assets": 500000.0, "assets_boy": 450000.0, "participants": 15, "participants_eoy": 16 },
              "2019": { "assets": 600000.0, "assets_boy": 500000.0, "participants": 16, "participants_eoy": 17 },
              "2020": { "assets": 700000.0, "assets_boy": 600000.0, "participants": 17, "participants_eoy": 18 },
              "2021": { "assets": 800000.0, "assets_boy": 700000.0, "participants": 18, "participants_eoy": 19 },
              "2022": { "assets": 900000.0, "assets_boy": 800000.0, "participants": 19, "participants_eoy": 20 },
              "2023": { "assets": 1000000.0, "assets_boy": 900000.0, "participants": 20, "participants_eoy": 21 },
              "2024": { "assets": 1100000.0, "assets_boy": 1000000.0, "participants": 21, "participants_eoy": 22 },
              "2025": { "assets": 1200000.0, "assets_boy": 1100000.0, "participants": 22, "participants_eoy": 23 }
            },
            "compliance_flags_by_year": {
              "2018": 0,
              "2019": 0,
              "2020": 0,
              "2021": 0,
              "2022": 1,
              "2023": 0,
              "2024": 0,
              "2025": 0
            },
            "form_5500_links_by_year": {
              "2018": "https://efast.dol.gov/5500/gardenstate/2018",
              "2019": "https://efast.dol.gov/5500/gardenstate/2019",
              "2020": "https://efast.dol.gov/5500/gardenstate/2020",
              "2021": "https://efast.dol.gov/5500/gardenstate/2021",
              "2022": "https://efast.dol.gov/5500/gardenstate/2022",
              "2023": "https://efast.dol.gov/5500/gardenstate/2023",
              "2024": "https://efast.dol.gov/5500/gardenstate/2024",
              "2025": "https://efast.dol.gov/5500/gardenstate/2025"
            },
            "growth_rate_by_year": {
              "2019": 20.0,
              "2020": 16.7,
              "2021": 14.3,
              "2022": 12.5,
              "2023": 11.1,
              "2024": 10.0,
              "2025": 9.1
            },
            "estimated_growth_rate": 10.0,
            "odds_of_zero_assets": 0.15,
            "plan_health_score": "B+",
            "individual_wealth_score": "Medium",
            "dollars_per_participant": {
              "2023": 47619.0,
              "2024": 50000.0,
              "2025": 52173.9
            }
          },
          {
            "ein": "360556677",
            "pn": "006",
            "plan_name": "Chicago Creative Agency 401(k) Plan",
            "sponsor_name": "Chicago Creative Agency LLC",
            "address": "800 Michigan Ave",
            "city": "Chicago",
            "state": "IL",
            "zip": "60611",
            "phone_number": "312-555-6677",
            "plan_codes": "2E,3D,2J",
            "plan_type": "Single-Employer plan",
            "plan_sponsor_industry": "Marketing",
            "yearly_data": {
              "2018": { "assets": 300000.0, "assets_boy": 250000.0, "participants": 10, "participants_eoy": 12 },
              "2019": { "assets": 350000.0, "assets_boy": 300000.0, "participants": 12, "participants_eoy": 13 },
              "2020": { "assets": 400000.0, "assets_boy": 350000.0, "participants": 13, "participants_eoy": 14 },
              "2021": { "assets": 450000.0, "assets_boy": 400000.0, "participants": 14, "participants_eoy": 15 },
              "2022": { "assets": 500000.0, "assets_boy": 450000.0, "participants": 15, "participants_eoy": 16 },
              "2023": { "assets": 550000.0, "assets_boy": 500000.0, "participants": 16, "participants_eoy": 17 },
              "2024": { "assets": 600000.0, "assets_boy": 550000.0, "participants": 17, "participants_eoy": 18 },
              "2025": { "assets": 650000.0, "assets_boy": 600000.0, "participants": 18, "participants_eoy": 19 }
            },
            "compliance_flags_by_year": {
              "2018": 0,
              "2019": 0,
              "2020": 0,
              "2021": 0,
              "2022": 0,
              "2023": 1,
              "2024": 0,
              "2025": 0
            },
            "form_5500_links_by_year": {
              "2018": "https://efast.dol.gov/5500/chicago/2018",
              "2019": "https://efast.dol.gov/5500/chicago/2019",
              "2020": "https://efast.dol.gov/5500/chicago/2020",
              "2021": "https://efast.dol.gov/5500/chicago/2021",
              "2022": "https://efast.dol.gov/5500/chicago/2022",
              "2023": "https://efast.dol.gov/5500/chicago/2023",
              "2024": "https://efast.dol.gov/5500/chicago/2024",
              "2025": "https://efast.dol.gov/5500/chicago/2025"
            },
            "growth_rate_by_year": {
              "2019": 16.7,
              "2020": 14.3,
              "2021": 12.5,
              "2022": 11.1,
              "2023": 10.0,
              "2024": 9.1,
              "2025": 8.3
            },
            "estimated_growth_rate": 9.0,
            "odds_of_zero_assets": 0.12,
            "plan_health_score": "B",
            "individual_wealth_score": "Low",
            "dollars_per_participant": {
              "2023": 32352.9,
              "2024": 33333.3,
              "2025": 34210.5
            }
          },
          {
            "ein": "480223344",
            "pn": "007",
            "plan_name": "Kansas City Engineering 401(k) Plan",
            "sponsor_name": "KC Engineering Group",
            "address": "123 Main St",
            "city": "Kansas City",
            "state": "MO",
            "zip": "64105",
            "phone_number": "816-555-2233",
            "plan_codes": "2E,3D,2F",
            "plan_type": "Single-Employer plan",
            "plan_sponsor_industry": "Engineering",
            "yearly_data": {
              "2018": { "assets": 400000.0, "assets_boy": 350000.0, "participants": 8, "participants_eoy": 9 },
              "2019": { "assets": 450000.0, "assets_boy": 400000.0, "participants": 9, "participants_eoy": 10 },
              "2020": { "assets": 500000.0, "assets_boy": 450000.0, "participants": 10, "participants_eoy": 11 },
              "2021": { "assets": 600000.0, "assets_boy": 500000.0, "participants": 11, "participants_eoy": 12 },
              "2022": { "assets": 700000.0, "assets_boy": 600000.0, "participants": 12, "participants_eoy": 13 },
              "2023": { "assets": 800000.0, "assets_boy": 700000.0, "participants": 13, "participants_eoy": 14 },
              "2024": { "assets": 900000.0, "assets_boy": 800000.0, "participants": 14, "participants_eoy": 15 },
              "2025": { "assets": 1000000.0, "assets_boy": 900000.0, "participants": 15, "participants_eoy": 16 }
            },
            "compliance_flags_by_year": {
              "2018": 0,
              "2019": 0,
              "2020": 0,
              "2021": 0,
              "2022": 0,
              "2023": 0,
              "2024": 1,
              "2025": 0
            },
            "form_5500_links_by_year": {
              "2018": "https://efast.dol.gov/5500/kceng/2018",
              "2019": "https://efast.dol.gov/5500/kceng/2019",
              "2020": "https://efast.dol.gov/5500/kceng/2020",
              "2021": "https://efast.dol.gov/5500/kceng/2021",
              "2022": "https://efast.dol.gov/5500/kceng/2022",
              "2023": "https://efast.dol.gov/5500/kceng/2023",
              "2024": "https://efast.dol.gov/5500/kceng/2024",
              "2025": "https://efast.dol.gov/5500/kceng/2025"
            },
            "growth_rate_by_year": {
              "2019": 12.5,
              "2020": 11.1,
              "2021": 20.0,
              "2022": 16.7,
              "2023": 14.3,
              "2024": 12.5,
              "2025": 11.1
            },
            "estimated_growth_rate": 13.0,
            "odds_of_zero_assets": 0.08,
            "plan_health_score": "A",
            "individual_wealth_score": "Medium",
            "dollars_per_participant": {
              "2023": 57142.9,
              "2024": 60000.0,
              "2025": 62500.0
            }
          },
          {
            "ein": "540334455",
            "pn": "008",
            "plan_name": "Virginia Tech Innovators 401(k) Plan",
            "sponsor_name": "Virginia Tech Innovators LLC",
            "address": "500 College Ave",
            "city": "Blacksburg",
            "state": "VA",
            "zip": "24060",
            "phone_number": "540-555-3344",
            "plan_codes": "2E,3D,2J",
            "plan_type": "Single-Employer plan",
            "plan_sponsor_industry": "Education",
            "yearly_data": {
              "2018": { "assets": 200000.0, "assets_boy": 180000.0, "participants": 7, "participants_eoy": 8 },
              "2019": { "assets": 250000.0, "assets_boy": 200000.0, "participants": 8, "participants_eoy": 9 },
              "2020": { "assets": 300000.0, "assets_boy": 250000.0, "participants": 9, "participants_eoy": 10 },
              "2021": { "assets": 350000.0, "assets_boy": 300000.0, "participants": 10, "participants_eoy": 11 },
              "2022": { "assets": 400000.0, "assets_boy": 350000.0, "participants": 11, "participants_eoy": 12 },
              "2023": { "assets": 450000.0, "assets_boy": 400000.0, "participants": 12, "participants_eoy": 13 },
              "2024": { "assets": 500000.0, "assets_boy": 450000.0, "participants": 13, "participants_eoy": 14 },
              "2025": { "assets": 550000.0, "assets_boy": 500000.0, "participants": 14, "participants_eoy": 15 }
            },
            "compliance_flags_by_year": {
              "2018": 0,
              "2019": 0,
              "2020": 0,
              "2021": 0,
              "2022": 0,
              "2023": 0,
              "2024": 0,
              "2025": 1
            },
            "form_5500_links_by_year": {
              "2018": "https://efast.dol.gov/5500/vtinnovators/2018",
              "2019": "https://efast.dol.gov/5500/vtinnovators/2019",
              "2020": "https://efast.dol.gov/5500/vtinnovators/2020",
              "2021": "https://efast.dol.gov/5500/vtinnovators/2021",
              "2022": "https://efast.dol.gov/5500/vtinnovators/2022",
              "2023": "https://efast.dol.gov/5500/vtinnovators/2023",
              "2024": "https://efast.dol.gov/5500/vtinnovators/2024",
              "2025": "https://efast.dol.gov/5500/vtinnovators/2025"
            },
            "growth_rate_by_year": {
              "2019": 25.0,
              "2020": 20.0,
              "2021": 16.7,
              "2022": 14.3,
              "2023": 12.5,
              "2024": 11.1,
              "2025": 10.0
            },
            "estimated_growth_rate": 12.0,
            "odds_of_zero_assets": 0.09,
            "plan_health_score": "B+",
            "individual_wealth_score": "Medium",
            "dollars_per_participant": {
              "2023": 34615.4,
              "2024": 35714.3,
              "2025": 36666.7
            }
          },
          {
            "ein": "250445566",
            "pn": "009",
            "plan_name": "Pittsburgh Steelworkers 401(k) Plan",
            "sponsor_name": "Pittsburgh Steelworkers Union",
            "address": "100 Steel St",
            "city": "Pittsburgh",
            "state": "PA",
            "zip": "15222",
            "phone_number": "412-555-4455",
            "plan_codes": "2E,3D,2F",
            "plan_type": "Multi-Employer plan",
            "plan_sponsor_industry": "Manufacturing",
            "yearly_data": {
              "2018": { "assets": 2000000.0, "assets_boy": 1800000.0, "participants": 100, "participants_eoy": 110 },
              "2019": { "assets": 2200000.0, "assets_boy": 2000000.0, "participants": 110, "participants_eoy": 120 },
              "2020": { "assets": 2400000.0, "assets_boy": 2200000.0, "participants": 120, "participants_eoy": 130 },
              "2021": { "assets": 2600000.0, "assets_boy": 2400000.0, "participants": 130, "participants_eoy": 140 },
              "2022": { "assets": 2800000.0, "assets_boy": 2600000.0, "participants": 140, "participants_eoy": 150 },
              "2023": { "assets": 3000000.0, "assets_boy": 2800000.0, "participants": 150, "participants_eoy": 160 },
              "2024": { "assets": 3200000.0, "assets_boy": 3000000.0, "participants": 160, "participants_eoy": 170 },
              "2025": { "assets": 3400000.0, "assets_boy": 3200000.0, "participants": 170, "participants_eoy": 180 }
            },
            "compliance_flags_by_year": {
              "2018": 0,
              "2019": 1,
              "2020": 0,
              "2021": 0,
              "2022": 0,
              "2023": 0,
              "2024": 0,
              "2025": 0
            },
            "form_5500_links_by_year": {
              "2018": "https://efast.dol.gov/5500/pghsteel/2018",
              "2019": "https://efast.dol.gov/5500/pghsteel/2019",
              "2020": "https://efast.dol.gov/5500/pghsteel/2020",
              "2021": "https://efast.dol.gov/5500/pghsteel/2021",
              "2022": "https://efast.dol.gov/5500/pghsteel/2022",
              "2023": "https://efast.dol.gov/5500/pghsteel/2023",
              "2024": "https://efast.dol.gov/5500/pghsteel/2024",
              "2025": "https://efast.dol.gov/5500/pghsteel/2025"
            },
            "growth_rate_by_year": {
              "2019": 10.0,
              "2020": 9.1,
              "2021": 8.3,
              "2022": 7.7,
              "2023": 7.1,
              "2024": 6.7,
              "2025": 6.3
            },
            "estimated_growth_rate": 7.0,
            "odds_of_zero_assets": 0.02,
            "plan_health_score": "A",
            "individual_wealth_score": "High",
            "dollars_per_participant": {
              "2023": 18750.0,
              "2024": 18823.5,
              "2025": 18888.9
            }
          },
          {
            "ein": "650556677",
            "pn": "010",
            "plan_name": "Miami Hospitality Group 401(k) Plan",
            "sponsor_name": "Miami Hospitality Group Inc.",
            "address": "200 Ocean Dr",
            "city": "Miami Beach",
            "state": "FL",
            "zip": "33139",
            "phone_number": "305-555-5566",
            "plan_codes": "2E,3D,2J",
            "plan_type": "Single-Employer plan",
            "plan_sponsor_industry": "Hospitality",
            "yearly_data": {
              "2018": { "assets": 350000.0, "assets_boy": 300000.0, "participants": 14, "participants_eoy": 15 },
              "2019": { "assets": 400000.0, "assets_boy": 350000.0, "participants": 15, "participants_eoy": 16 },
              "2020": { "assets": 450000.0, "assets_boy": 400000.0, "participants": 16, "participants_eoy": 17 },
              "2021": { "assets": 500000.0, "assets_boy": 450000.0, "participants": 17, "participants_eoy": 18 },
              "2022": { "assets": 550000.0, "assets_boy": 500000.0, "participants": 18, "participants_eoy": 19 },
              "2023": { "assets": 600000.0, "assets_boy": 550000.0, "participants": 19, "participants_eoy": 20 },
              "2024": { "assets": 650000.0, "assets_boy": 600000.0, "participants": 20, "participants_eoy": 21 },
              "2025": { "assets": 700000.0, "assets_boy": 650000.0, "participants": 21, "participants_eoy": 22 }
            },
            "compliance_flags_by_year": {
              "2018": 0,
              "2019": 0,
              "2020": 0,
              "2021": 0,
              "2022": 0,
              "2023": 0,
              "2024": 0,
              "2025": 0
            },
            "form_5500_links_by_year": {
              "2018": "https://efast.dol.gov/5500/miamihosp/2018",
              "2019": "https://efast.dol.gov/5500/miamihosp/2019",
              "2020": "https://efast.dol.gov/5500/miamihosp/2020",
              "2021": "https://efast.dol.gov/5500/miamihosp/2021",
              "2022": "https://efast.dol.gov/5500/miamihosp/2022",
              "2023": "https://efast.dol.gov/5500/miamihosp/2023",
              "2024": "https://efast.dol.gov/5500/miamihosp/2024",
              "2025": "https://efast.dol.gov/5500/miamihosp/2025"
            },
            "growth_rate_by_year": {
              "2019": 14.3,
              "2020": 12.5,
              "2021": 11.1,
              "2022": 10.0,
              "2023": 9.1,
              "2024": 8.3,
              "2025": 7.7
            },
            "estimated_growth_rate": 10.0,
            "odds_of_zero_assets": 0.07,
            "plan_health_score": "B",
            "individual_wealth_score": "Medium",
            "dollars_per_participant": {
              "2023": 30000.0,
              "2024": 30952.4,
              "2025": 31818.2
            }
          },
          {
            "ein": "720667788",
            "pn": "011",
            "plan_name": "Baton Rouge Medical 401(k) Plan",
            "sponsor_name": "Baton Rouge Medical Center",
            "address": "400 Hospital Dr",
            "city": "Baton Rouge",
            "state": "LA",
            "zip": "70808",
            "phone_number": "225-555-6677",
            "plan_codes": "2E,3D,2F",
            "plan_type": "Single-Employer plan",
            "plan_sponsor_industry": "Healthcare",
            "yearly_data": {
              "2018": { "assets": 800000.0, "assets_boy": 700000.0, "participants": 30, "participants_eoy": 32 },
              "2019": { "assets": 900000.0, "assets_boy": 800000.0, "participants": 32, "participants_eoy": 34 },
              "2020": { "assets": 1000000.0, "assets_boy": 900000.0, "participants": 34, "participants_eoy": 36 },
              "2021": { "assets": 1100000.0, "assets_boy": 1000000.0, "participants": 36, "participants_eoy": 38 },
              "2022": { "assets": 1200000.0, "assets_boy": 1100000.0, "participants": 38, "participants_eoy": 40 },
              "2023": { "assets": 1300000.0, "assets_boy": 1200000.0, "participants": 40, "participants_eoy": 42 },
              "2024": { "assets": 1400000.0, "assets_boy": 1300000.0, "participants": 42, "participants_eoy": 44 },
              "2025": { "assets": 1500000.0, "assets_boy": 1400000.0, "participants": 44, "participants_eoy": 46 }
            },
            "compliance_flags_by_year": {
              "2018": 0,
              "2019": 0,
              "2020": 0,
              "2021": 0,
              "2022": 0,
              "2023": 0,
              "2024": 0,
              "2025": 0
            },
            "form_5500_links_by_year": {
              "2018": "https://efast.dol.gov/5500/brmed/2018",
              "2019": "https://efast.dol.gov/5500/brmed/2019",
              "2020": "https://efast.dol.gov/5500/brmed/2020",
              "2021": "https://efast.dol.gov/5500/brmed/2021",
              "2022": "https://efast.dol.gov/5500/brmed/2022",
              "2023": "https://efast.dol.gov/5500/brmed/2023",
              "2024": "https://efast.dol.gov/5500/brmed/2024",
              "2025": "https://efast.dol.gov/5500/brmed/2025"
            },
            "growth_rate_by_year": {
              "2019": 12.5,
              "2020": 11.1,
              "2021": 10.0,
              "2022": 9.1,
              "2023": 8.3,
              "2024": 7.7,
              "2025": 7.1
            },
            "estimated_growth_rate": 9.0,
            "odds_of_zero_assets": 0.03,
            "plan_health_score": "A",
            "individual_wealth_score": "High",
            "dollars_per_participant": {
              "2023": 30952.4,
              "2024": 31818.2,
              "2025": 32608.7
            }
          },
          {
            "ein": "990778899",
            "pn": "012",
            "plan_name": "Seattle Tech Collective 401(k) Plan",
            "sponsor_name": "Seattle Tech Collective",
            "address": "1000 1st Ave",
            "city": "Seattle",
            "state": "WA",
            "zip": "98104",
            "phone_number": "206-555-7788",
            "plan_codes": "2E,3D,2J",
            "plan_type": "Multi-Employer plan",
            "plan_sponsor_industry": "Technology",
            "yearly_data": {
              "2018": { "assets": 1200000.0, "assets_boy": 1000000.0, "participants": 50, "participants_eoy": 55 },
              "2019": { "assets": 1400000.0, "assets_boy": 1200000.0, "participants": 55, "participants_eoy": 60 },
              "2020": { "assets": 1600000.0, "assets_boy": 1400000.0, "participants": 60, "participants_eoy": 65 },
              "2021": { "assets": 1800000.0, "assets_boy": 1600000.0, "participants": 65, "participants_eoy": 70 },
              "2022": { "assets": 2000000.0, "assets_boy": 1800000.0, "participants": 70, "participants_eoy": 75 },
              "2023": { "assets": 2200000.0, "assets_boy": 2000000.0, "participants": 75, "participants_eoy": 80 },
              "2024": { "assets": 2400000.0, "assets_boy": 2200000.0, "participants": 80, "participants_eoy": 85 },
              "2025": { "assets": 2600000.0, "assets_boy": 2400000.0, "participants": 85, "participants_eoy": 90 }
            },
            "compliance_flags_by_year": {
              "2018": 0,
              "2019": 0,
              "2020": 0,
              "2021": 0,
              "2022": 0,
              "2023": 0,
              "2024": 0,
              "2025": 0
            },
            "form_5500_links_by_year": {
              "2018": "https://efast.dol.gov/5500/seattletech/2018",
              "2019": "https://efast.dol.gov/5500/seattletech/2019",
              "2020": "https://efast.dol.gov/5500/seattletech/2020",
              "2021": "https://efast.dol.gov/5500/seattletech/2021",
              "2022": "https://efast.dol.gov/5500/seattletech/2022",
              "2023": "https://efast.dol.gov/5500/seattletech/2023",
              "2024": "https://efast.dol.gov/5500/seattletech/2024",
              "2025": "https://efast.dol.gov/5500/seattletech/2025"
            },
            "growth_rate_by_year": {
              "2019": 16.7,
              "2020": 14.3,
              "2021": 12.5,
              "2022": 11.1,
              "2023": 10.0,
              "2024": 9.1,
              "2025": 8.3
            },
            "estimated_growth_rate": 11.0,
            "odds_of_zero_assets": 0.01,
            "plan_health_score": "A",
            "individual_wealth_score": "High",
            "dollars_per_participant": {
              "2023": 27500.0,
              "2024": 28235.3,
              "2025": 28888.9
            }
          },
          {
            "ein": "810889900",
            "pn": "013",
            "plan_name": "Montana Ranchers 401(k) Plan",
            "sponsor_name": "Montana Ranchers Association",
            "address": "50 Big Sky Rd",
            "city": "Bozeman",
            "state": "MT",
            "zip": "59715",
            "phone_number": "406-555-8899",
            "plan_codes": "2E,3D",
            "plan_type": "Single-Employer plan",
            "plan_sponsor_industry": "Agriculture",
            "yearly_data": {
              "2018": { "assets": 100000.0, "assets_boy": 90000.0, "participants": 4, "participants_eoy": 5 },
              "2019": { "assets": 120000.0, "assets_boy": 100000.0, "participants": 5, "participants_eoy": 6 },
              "2020": { "assets": 140000.0, "assets_boy": 120000.0, "participants": 6, "participants_eoy": 7 },
              "2021": { "assets": 160000.0, "assets_boy": 140000.0, "participants": 7, "participants_eoy": 8 },
              "2022": { "assets": 180000.0, "assets_boy": 160000.0, "participants": 8, "participants_eoy": 9 },
              "2023": { "assets": 200000.0, "assets_boy": 180000.0, "participants": 9, "participants_eoy": 10 },
              "2024": { "assets": 220000.0, "assets_boy": 200000.0, "participants": 10, "participants_eoy": 11 },
              "2025": { "assets": 240000.0, "assets_boy": 220000.0, "participants": 11, "participants_eoy": 12 }
            },
            "compliance_flags_by_year": {
              "2018": 0,
              "2019": 0,
              "2020": 0,
              "2021": 0,
              "2022": 0,
              "2023": 0,
              "2024": 0,
              "2025": 0
            }
        }
        
      ];
      
      setData(mockData);
      setFilteredData(mockData);
    } catch (error) {
      message.error('Failed to fetch plans data');
      console.error('Error fetching plans:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  // Apply Filters
  useEffect(() => {
    let filtered = [...data];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(plan => 
        plan.plan_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        plan.sponsor_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        plan.city.toLowerCase().includes(searchTerm.toLowerCase()) ||
        plan.ein.includes(searchTerm)
      );
    }

    // State filter
    if (filters.state.length > 0) {
      filtered = filtered.filter(plan => filters.state.includes(plan.state));
    }

    // City filter
    if (filters.city.length > 0) {
      filtered = filtered.filter(plan => filters.city.includes(plan.city));
    }

    // EIN filter
    if (filters.ein) {
      filtered = filtered.filter(plan => plan.ein.includes(filters.ein));
    }

    // PN filter
    if (filters.pn) {
      filtered = filtered.filter(plan => plan.pn.includes(filters.pn));
    }

    // Asset range filter
    filtered = filtered.filter(plan => {
      const latestYear = Object.keys(plan.yearly_data).sort().pop();
      const assets = plan.yearly_data[latestYear]?.assets || 0;
      return assets >= filters.assetRange[0] && assets <= filters.assetRange[1];
    });

    // Participant range filter
    filtered = filtered.filter(plan => {
      const latestYear = Object.keys(plan.yearly_data).sort().pop();
      const participants = plan.yearly_data[latestYear]?.participants_eoy || 0;
      return participants >= filters.participantRange[0] && participants <= filters.participantRange[1];
    });

    // Estimated growth filter
    filtered = filtered.filter(plan => {
      const growthRate = plan.estimated_growth_rate || 0;
      return growthRate >= filters.estimatedGrowthRange[0] && growthRate <= filters.estimatedGrowthRange[1];
    });

    // Odds filter
    filtered = filtered.filter(plan => {
      const odds = (plan.odds_of_zero_assets || 0) * 100;
      return odds >= filters.oddsRange[0] && odds <= filters.oddsRange[1];
    });

    // DPP filter
    filtered = filtered.filter(plan => {
      if (!plan.dollars_per_participant || Object.keys(plan.dollars_per_participant).length === 0) {
        return filters.dppRange[0] === 0; // Include plans with no DPP data if minimum is 0
      }
      const latestYear = Object.keys(plan.dollars_per_participant).sort().pop();
      const dpp = plan.dollars_per_participant[latestYear] || 0;
      return dpp >= filters.dppRange[0] && dpp <= filters.dppRange[1];
    });

    // Plan codes filter
    if (filters.planCodes.length > 0) {
      filtered = filtered.filter(plan => {
        const planCodes = plan.plan_codes.split(',');
        return filters.planCodes.some(code => planCodes.includes(code));
      });
    }

    // Plan type filter
    if (filters.planType.length > 0) {
      filtered = filtered.filter(plan => filters.planType.includes(plan.plan_type));
    }

    // Health score filter
    if (filters.healthScore.length > 0) {
      filtered = filtered.filter(plan => filters.healthScore.includes(plan.plan_health_score));
    }

    // Wealth score filter
    if (filters.wealthScore.length > 0) {
      filtered = filtered.filter(plan => filters.wealthScore.includes(plan.individual_wealth_score));
    }

    // Industry filter
    if (filters.industry.length > 0) {
      filtered = filtered.filter(plan => filters.industry.includes(plan.plan_sponsor_industry));
    }

    setFilteredData(filtered);
  }, [searchTerm, filters, data]);

  // Table Columns
  const columns = [
    {
      title: 'Plan Name',
      dataIndex: 'plan_name',
      key: 'plan_name',
      width: 300,
      render: (text, record) => (
        <div>
          <Text strong style={{ fontSize: '14px', color: '#1a202c' }}>{text}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: '12px' }}>{record.sponsor_name}</Text>
        </div>
      ),
      sorter: (a, b) => a.plan_name.localeCompare(b.plan_name),
    },
    {
      title: 'Location',
      key: 'location',
      width: 200,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text style={{ fontSize: '13px' }}>
            <EnvironmentOutlined style={{ marginRight: 4 }} />
            {record.city}, {record.state}
          </Text>
          <Text type="secondary" style={{ fontSize: '12px' }}>{record.zip}</Text>
        </Space>
      ),
      filters: [...new Set(data.map(item => item.state))].map(state => ({
        text: state,
        value: state,
      })),
      onFilter: (value, record) => record.state === value,
    },
    {
      title: 'Assets',
      key: 'assets',
      width: 150,
      render: (_, record) => {
        const latestYear = Object.keys(record.yearly_data).sort().pop();
        const assets = record.yearly_data[latestYear]?.assets || 0;
        const prevYear = (parseInt(latestYear) - 1).toString();
        const prevAssets = record.yearly_data[prevYear]?.assets || 0;
        const growth = prevAssets > 0 ? ((assets - prevAssets) / prevAssets * 100).toFixed(1) : 0;
        
        return (
          <Space direction="vertical" size={0}>
            <Text strong style={{ fontSize: '14px' }}>
              ${(assets / 1000000).toFixed(2)}M
            </Text>
            <Text type={growth >= 0 ? 'success' : 'danger'} style={{ fontSize: '12px' }}>
              {growth >= 0 ? '↑' : '↓'} {Math.abs(growth)}%
            </Text>
          </Space>
        );
      },
      sorter: (a, b) => {
        const latestYear = Object.keys(a.yearly_data).sort().pop();
        const aAssets = a.yearly_data[latestYear]?.assets || 0;
        const bAssets = b.yearly_data[latestYear]?.assets || 0;
        return aAssets - bAssets;
      },
    },
    {
      title: 'Participants',
      key: 'participants',
      width: 120,
      render: (_, record) => {
        const latestYear = Object.keys(record.yearly_data).sort().pop();
        const participants = record.yearly_data[latestYear]?.participants_eoy || 0;
        return (
          <Space>
            <UserOutlined />
            <Text style={{ fontSize: '14px' }}>{participants}</Text>
          </Space>
        );
      },
      sorter: (a, b) => {
        const latestYear = Object.keys(a.yearly_data).sort().pop();
        const aParticipants = a.yearly_data[latestYear]?.participants_eoy || 0;
        const bParticipants = b.yearly_data[latestYear]?.participants_eoy || 0;
        return aParticipants - bParticipants;
      },
    },
    {
      title: 'Plan Codes',
      dataIndex: 'plan_codes',
      key: 'plan_codes',
      width: 200,
      render: (codes) => (
        <Space size={4} wrap>
          {(Array.isArray(codes) ? codes : codes.split(',')).map(code => (
            <Tooltip key={code} title={PLAN_CODES_MAP[code] || code}>
              <Tag color="blue" style={{ margin: 0 }}>{code}</Tag>
            </Tooltip>
          ))}
        </Space>
      ),
    },
    {
      title: 'Plan Health Score',
      key: 'plan_health_score',
      width: 180,
      render: (_, record) => (
        <Tag 
          icon={<SafetyCertificateOutlined />}
          color={record.plan_health_score === 'A' ? 'success' : record.plan_health_score === 'B+' ? 'warning' : 'error'}
        >
          {record.plan_health_score}
        </Tag>
      ),
      sorter: (a, b) => a.plan_health_score.localeCompare(b.plan_health_score),
      filters: [
        { text: 'A', value: 'A' },
        { text: 'B+', value: 'B+' },
        { text: 'B', value: 'B' },
        { text: 'C', value: 'C' },
      ],
      onFilter: (value, record) => record.plan_health_score === value,
    },
    {
      title: 'Individual Wealth Score',
      key: 'individual_wealth_score',
      width: 200,
      render: (_, record) => (
        <Tag 
          icon={<TrophyOutlined />}
          color={record.individual_wealth_score === 'High' ? 'green' : record.individual_wealth_score === 'Medium' ? 'blue' : 'default'}
        >
          {record.individual_wealth_score}
        </Tag>
      ),
      sorter: (a, b) => a.individual_wealth_score.localeCompare(b.individual_wealth_score),
      filters: [
        { text: 'High', value: 'High' },
        { text: 'Medium', value: 'Medium' },
        { text: 'Low', value: 'Low' },
      ],
      onFilter: (value, record) => record.individual_wealth_score === value,
    },
  ];

  const handleViewDetails = (plan) => {
    setSelectedPlan(plan);
    setModalVisible(true);
  };

  const handleRowClick = (record) => {
    handleViewDetails(record);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const renderPlanDetails = () => {
    if (!selectedPlan) return null;

    const latestYear = Object.keys(selectedPlan.yearly_data).sort().pop();
    const latestData = selectedPlan.yearly_data[latestYear];
    const years = Object.keys(selectedPlan.yearly_data).sort();
    
    // Calculate additional metrics
    const totalGrowth = ((latestData.assets - selectedPlan.yearly_data[years[0]].assets) / selectedPlan.yearly_data[years[0]].assets * 100).toFixed(1);
    const avgParticipants = Math.round(years.reduce((sum, year) => sum + selectedPlan.yearly_data[year].participants_eoy, 0) / years.length);
    const complianceRate = (Object.values(selectedPlan.compliance_flags_by_year).filter(flag => flag === 0).length / Object.values(selectedPlan.compliance_flags_by_year).length * 100).toFixed(0);
    
    // Prepare chart data
    const pieData = [
      { name: 'Compliant Years', value: Object.values(selectedPlan.compliance_flags_by_year).filter(f => f === 0).length, color: '#52c41a' },
      { name: 'Non-Compliant Years', value: Object.values(selectedPlan.compliance_flags_by_year).filter(f => f === 1).length, color: '#ff4d4f' }
    ];

    return (
      <div style={{ padding: 0, background: 'transparent', minHeight: '95vh' }}>
        {/* Premium Header Section */}
        <div style={{ 
          background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
          padding: '40px',
          borderRadius: '10x 10px 0 0',
          color: 'white',
          position: 'relative',
          overflow: 'auto'
        }}>
          <div style={{ position: 'absolute', top: -50, right: -50, opacity: 0.1 }}>
          </div>
          <Row align="middle" justify="space-between">
            <Col span={16}>
              <Title level={2} style={{ color: 'white', marginBottom: '8px', fontSize: '32px' }}>
                {selectedPlan.plan_name}
              </Title>
              <Paragraph style={{ color: 'rgba(255,255,255,0.9)', fontSize: '18px', marginBottom: '16px' }}>
                {selectedPlan.sponsor_name}
              </Paragraph>
              <Space size="large" wrap>
                <Tag icon={<BuildOutlined />} color="white" style={{ padding: '6px 16px', fontSize: '14px', background: 'rgba(255,255,255,0.2)', border: 'none' }}>
                  {selectedPlan.plan_sponsor_industry}
                </Tag>
                <Tag icon={<InfoCircleOutlined />} style={{ padding: '6px 16px', fontSize: '14px', background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white' }}>
                  EIN: {selectedPlan.ein} | Plan: {selectedPlan.pn}
                </Tag>
                <Tag style={{ padding: '6px 16px', fontSize: '14px', background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white' }}>
                  {selectedPlan.plan_type}
                </Tag>
              </Space>
            </Col>
            <Col span={8} style={{ textAlign: 'right' }}>
              <div style={{ background: 'rgba(255,255,255,0.2)', borderRadius: '12px', padding: '20px' }}>
                <div style={{ marginBottom: '12px' }}>
                  <Text style={{ color: 'rgba(255,255,255,0.8)', fontSize: '12px' }}>PLAN HEALTH</Text>
                  <div style={{ fontSize: '36px', fontWeight: 'bold', color: 'white' }}>{selectedPlan.plan_health_score}</div>
                </div>
                <div>
                  <Text style={{ color: 'rgba(255,255,255,0.8)', fontSize: '12px' }}>WEALTH SCORE</Text>
                  <div style={{ fontSize: '24px', fontWeight: 'bold', color: 'white' }}>{selectedPlan.individual_wealth_score}</div>
                </div>
              </div>

              {/* Get Proposal Button */}
              <Button
                type="primary"
                icon={<FileTextOutlined />}
                style={{ marginTop: '16px' }}
                onClick={() => {
                  const latestYearBtn = Object.keys(selectedPlan.yearly_data).sort().pop();
                  const latestYearData = selectedPlan.yearly_data[latestYearBtn];
                  
                  // Create Form 5500 file object from the plan's form links
                  const form5500Link = selectedPlan.form_5500_links_by_year?.[latestYearBtn];
                  let form5500File = null;
                  if (form5500Link) {
                    form5500File = {
                      name: `Form 5500 - ${selectedPlan.plan_name} (${latestYearBtn}).pdf`,
                      url: form5500Link,
                      isUrlFile: true,
                      filingYear: latestYearBtn,
                      dateReceived: selectedPlan.yearly_data[latestYearBtn]?.date_received || null
                    };
                  }
                  
                  const prefilledData = {
                    planName: selectedPlan.plan_name,
                    sponsorName: selectedPlan.sponsor_name,
                    sponsorAddress: `${selectedPlan.address}, ${selectedPlan.city}, ${selectedPlan.state} ${selectedPlan.zip}`,
                    sponsorIndustry: selectedPlan.plan_sponsor_industry,
                    totalParticipants: latestYearData.participants_eoy || latestYearData.participants || 0,
                    activeParticipants: latestYearData.participants || latestYearData.participants_eoy || 0,
                    totalAssets: latestYearData.assets,
                    form5500: form5500File,
                    _sourceData: {
                      ein: selectedPlan.ein,
                      mostRecentFilingYear: latestYearBtn
                    }
                  };
                  navigate('/proposal-automation', { state: { prefilledData } });
                  setModalVisible(false);
                }}
              >
                Get Proposal
              </Button>
            </Col>
          </Row>
        </div>

        {/* Key Metrics Cards */}
        <div style={{ padding: '0 40px', marginTop: '-30px', marginBottom: '20px' }}>
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={6}>
              <Card style={{ borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.08)', textAlign: 'center' }}>
                <div style={{ padding: '20px' }}>
                  <Avatar style={{ backgroundColor: '#667eea', marginBottom: '12px' }} size={48} icon={<DollarOutlined />} />
                  <Statistic
                    title="Current Assets"
                    value={latestData.assets}
                    formatter={(value) => formatCurrency(value)}
                    valueStyle={{ fontSize: '24px', fontWeight: 'bold' }}
                  />
                  <Progress 
                    percent={totalGrowth} 
                    strokeColor="#667eea" 
                    showInfo={false}
                    style={{ marginTop: '8px' }}
                  />
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    {totalGrowth > 0 ? <ArrowUpOutlined /> : <ArrowDownOutlined />} {Math.abs(totalGrowth)}% total growth
                  </Text>
                </div>
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card style={{ borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.08)', textAlign: 'center' }}>
                <div style={{ padding: '20px' }}>
                  <Avatar style={{ backgroundColor: '#764ba2', marginBottom: '12px' }} size={48} icon={<TeamOutlined />} />
                  <Statistic
                    title="Participants"
                    value={latestData.participants_eoy}
                    valueStyle={{ fontSize: '24px', fontWeight: 'bold' }}
                  />
                  <div style={{ marginTop: '8px' }}>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      Average: {avgParticipants} participants
                    </Text>
                  </div>
                  <div>
                    <Text style={{ fontSize: '14px', color: '#667eea' }}>
                      ${(latestData.assets / latestData.participants_eoy).toFixed(0)} per participant
                    </Text>
                  </div>
                </div>
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card style={{ borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.08)', textAlign: 'center' }}>
                <div style={{ padding: '20px' }}>
                  <Avatar style={{ backgroundColor: '#52c41a', marginBottom: '12px' }} size={48} icon={<SafetyCertificateOutlined />} />
                  <Statistic
                    title="Compliance Rate"
                    value={complianceRate}
                    suffix="%"
                    valueStyle={{ fontSize: '24px', fontWeight: 'bold', color: '#52c41a' }}
                  />
                  <div style={{ marginTop: '8px' }}>
                    <ResponsiveContainer width="100%" height={60}>
                      <PieChart>
                        <Pie
                          data={pieData}
                          cx="50%"
                          cy="50%"
                          innerRadius={15}
                          outerRadius={25}
                          paddingAngle={2}
                          dataKey="value"
                        >
                          {pieData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card style={{ borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.08)', textAlign: 'center' }}>
                <div style={{ padding: '20px' }}>
                  <Avatar style={{ backgroundColor: '#f5222d', marginBottom: '12px' }} size={48} icon={<WarningOutlined />} />
                  <Statistic
                    title="Risk Score"
                    value={selectedPlan.odds_of_zero_assets * 100}
                    suffix="%"
                    valueStyle={{ 
                      fontSize: '24px', 
                      fontWeight: 'bold',
                      color: selectedPlan.odds_of_zero_assets > 0.3 ? '#f5222d' : '#52c41a'
                    }}
                  />
                  <Progress 
                    percent={selectedPlan.odds_of_zero_assets * 100} 
                    strokeColor={selectedPlan.odds_of_zero_assets > 0.3 ? '#f5222d' : '#52c41a'}
                    showInfo={false}
                    style={{ marginTop: '8px' }}
                  />
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    Risk of zero assets
                  </Text>
                </div>
              </Card>
            </Col>
          </Row>
        </div>

        {/* Tabbed Content */}
        <div style={{ padding: '0 40px 40px' }}>
          <Tabs 
            defaultActiveKey="overview" 
            size="large"
            style={{ 
              background: 'white',
              borderRadius: '12px',
              padding: '24px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
            }}
            items={[
              {
                key: 'overview',
                label: (
                  <span>
                    <BarChartOutlined /> Overview
                  </span>
                ),
                children: (
                  <div>
                    <Row gutter={[24, 24]}>
                      <Col span={12}>
                        <Card title={<span><EnvironmentOutlined /> Contact Information</span>} style={{ borderRadius: '8px' }}>
                          <Space direction="vertical" size="large" style={{ width: '100%' }}>
                            <div>
                              <Text type="secondary">Address</Text>
                              <div style={{ fontSize: '16px', marginTop: '4px' }}>
                                {selectedPlan.address}<br />
                                {selectedPlan.city}, {selectedPlan.state} {selectedPlan.zip}
                              </div>
                            </div>
                            <div>
                              <Text type="secondary">Phone</Text>
                              <div style={{ fontSize: '16px', marginTop: '4px' }}>
                                <PhoneOutlined /> {selectedPlan.phone_number}
                              </div>
                            </div>
                          </Space>
                        </Card>
                        
                        <Card title={<span><InfoCircleOutlined /> Plan Details</span>} style={{ marginTop: '16px', borderRadius: '8px' }}>
                          <Space direction="vertical" size="small" style={{ width: '100%' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Text type="secondary">Plan Type</Text>
                              <Text strong>{selectedPlan.plan_type}</Text>
                            </div>
                            <Divider style={{ margin: '8px 0' }} />
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Text type="secondary">Industry</Text>
                              <Text strong>{selectedPlan.plan_sponsor_industry}</Text>
                            </div>
                            <Divider style={{ margin: '8px 0' }} />
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Text type="secondary">Plan Codes</Text>
                              <Space>
                                {selectedPlan.plan_codes.split(',').map(code => (
                                  <Tooltip key={code} title={PLAN_CODES_MAP[code.trim()] || code}>
                                    <Tag color="blue">{code.trim()}</Tag>
                                  </Tooltip>
                                ))}
                              </Space>
                            </div>
                            <Divider style={{ margin: '8px 0' }} />
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Text type="secondary">Estimated Growth Rate</Text>
                              <Text strong style={{ color: '#52c41a' }}>{selectedPlan.estimated_growth_rate}%</Text>
                            </div>
                          </Space>
                        </Card>
                      </Col>
                      
                      <Col span={12}>
                        <Card title={<span><FundOutlined /> Asset Growth Analysis</span>} style={{ height: '100%', borderRadius: '8px' }}>
                          <ResponsiveContainer width="100%" height={300}>
                            <BarChart
                              data={years.map(year => ({
                                year,
                                assets: selectedPlan.yearly_data[year].assets / 1000000,
                                participants: selectedPlan.yearly_data[year].participants_eoy
                              }))}
                            >
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis dataKey="year" />
                              <YAxis yAxisId="assets" orientation="left" tickFormatter={(value) => `$${value}M`} />
                              <YAxis yAxisId="participants" orientation="right" />
                              <RechartsTooltip />
                              <Bar yAxisId="assets" dataKey="assets" fill="#667eea" name="Assets ($M)" />
                              <Bar yAxisId="participants" dataKey="participants" fill="#764ba2" name="Participants" />
                            </BarChart>
                          </ResponsiveContainer>
                        </Card>
                      </Col>
                    </Row>
                  </div>
                )
              },
              {
                key: 'performance',
                label: (
                  <span>
                    <LineChartOutlined /> Performance
                  </span>
                ),
                children: (
                  <div>
                    <Alert
                      message="Performance Overview"
                      description="Compare asset and participant growth side-by-side to understand plan performance trends."
                      type="info"
                      showIcon
                      style={{ marginBottom: '24px' }}
                    />
                    
                    <Row gutter={[24, 24]}>
                      {/* Growth Rate Comparison Chart */}
                      <Col span={16}>
                        <Card 
                          title={
                            <span>
                              <TrendingUp size={16} style={{ marginRight: '8px', color: '#667eea' }} />
                              Asset vs Participant Growth Comparison
                            </span>
                          }
                          style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.08)', borderRadius: '8px' }}
                        >
                          <ResponsiveContainer width="100%" height={400}>
                            <BarChart
                              data={(() => {
                                if (!selectedPlan || !selectedPlan.yearly_data) return [];
                                const years = Object.keys(selectedPlan.yearly_data).sort();
                                return years.map((year, index) => {
                                  const currentData = selectedPlan.yearly_data[year];
                                  const prevYear = years[index - 1];
                                  const prevData = prevYear ? selectedPlan.yearly_data[prevYear] : null;
                                  
                                  const assetGrowthRate = prevData && prevData.assets > 0 
                                    ? ((currentData.assets - prevData.assets) / prevData.assets * 100)
                                    : 0;
                                  
                                  const currentParticipants = currentData.participants_eoy || currentData.participants || 0;
                                  const prevParticipants = prevData ? (prevData.participants_eoy || prevData.participants || 0) : 0;
                                  
                                  const participantGrowthRate = prevData && prevParticipants > 0
                                    ? ((currentParticipants - prevParticipants) / prevParticipants * 100)
                                    : 0;
                                  
                                  return {
                                    year,
                                    assetGrowthRate: parseFloat(assetGrowthRate.toFixed(1)),
                                    participantGrowthRate: parseFloat(participantGrowthRate.toFixed(1)),
                                    assets: currentData.assets,
                                    participants: currentParticipants
                                  };
                                });
                              })()}
                              margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                            >
                              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                              <XAxis dataKey="year" stroke="#666" fontSize={12} tickLine={false} axisLine={false} />
                              <YAxis 
                                stroke="#666"
                                fontSize={12}
                                tickLine={false}
                                axisLine={false}
                                domain={[-10, 50]}
                                tickFormatter={(value) => `${value}%`}
                              />
                              <RechartsTooltip
                                contentStyle={{
                                  backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                  border: 'none',
                                  borderRadius: '8px',
                                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                                  padding: '12px'
                                }}
                                formatter={(value, name) => [
                                  `${value}%`, 
                                  name === 'assetGrowthRate' ? 'Asset Growth' : 'Participant Growth'
                                ]}
                              />
                              <Legend />
                              <Bar
                                dataKey="assetGrowthRate"
                                fill="#667eea"
                                name="Asset Growth Rate"
                                radius={[2, 2, 0, 0]}
                                animationDuration={1500}
                                animationEasing="ease-out"
                              />
                              <Bar
                                dataKey="participantGrowthRate"
                                fill="#764ba2"
                                name="Participant Growth Rate"
                                radius={[2, 2, 0, 0]}
                                animationDuration={1500}
                                animationEasing="ease-out"
                              />
                            </BarChart>
                          </ResponsiveContainer>
                        </Card>
                      </Col>

                      {/* Key Metrics Summary */}
                      <Col span={8}>
                        <Space direction="vertical" size={16} style={{ width: '100%' }}>
                          <Card 
                            title={
                              <span>
                                <DollarSign size={16} style={{ marginRight: '8px', color: '#4facfe' }} />
                                Current Assets
                              </span>
                            }
                            style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.08)', borderRadius: '8px' }}
                          >
                            <Statistic
                              value={(() => {
                                if (!selectedPlan || !selectedPlan.yearly_data) return 0;
                                const latestYear = Object.keys(selectedPlan.yearly_data).sort().pop();
                                return selectedPlan.yearly_data[latestYear]?.assets || 0;
                              })()}
                              formatter={(value) => `$${(value / 1000000).toFixed(1)}M`}
                              valueStyle={{ fontSize: '28px', fontWeight: 'bold', color: '#4facfe' }}
                            />
                            <div style={{ marginTop: '8px' }}>
                              <Text type="secondary" style={{ fontSize: '12px' }}>
                                Assets in Millions
                              </Text>
                            </div>
                          </Card>

                          <Card 
                            title={
                              <span>
                                <TeamOutlined style={{ marginRight: '8px', color: '#f093fb' }} />
                                Current Participants
                              </span>
                            }
                            style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.08)', borderRadius: '8px' }}
                          >
                            <Statistic
                              value={(() => {
                                if (!selectedPlan || !selectedPlan.yearly_data) return 0;
                                const latestYear = Object.keys(selectedPlan.yearly_data).sort().pop();
                                const latestData = selectedPlan.yearly_data[latestYear];
                                return latestData?.participants_eoy || latestData?.participants || 0;
                              })()}
                              valueStyle={{ fontSize: '28px', fontWeight: 'bold', color: '#f093fb' }}
                            />
                            <div style={{ marginTop: '8px' }}>
                              <Text type="secondary" style={{ fontSize: '12px' }}>
                                Active Participants
                              </Text>
                            </div>
                          </Card>

                          <Card 
                            title={
                              <span>
                                <Target size={16} style={{ marginRight: '8px', color: '#52c41a' }} />
                                Growth Performance
                              </span>
                            }
                            style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.08)', borderRadius: '8px' }}
                          >
                            <div style={{ textAlign: 'center' }}>
                              <div style={{ marginBottom: '12px' }}>
                                <Text type="secondary" style={{ fontSize: '12px' }}>Average Asset Growth</Text>
                                <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#667eea' }}>
                                  {(() => {
                                    if (!selectedPlan || !selectedPlan.yearly_data) return '0%';
                                    const years = Object.keys(selectedPlan.yearly_data).sort();
                                    let totalGrowth = 0;
                                    let growthCount = 0;
                                    
                                    years.forEach((year, index) => {
                                      if (index > 0) {
                                        const current = selectedPlan.yearly_data[year];
                                        const prev = selectedPlan.yearly_data[years[index - 1]];
                                        if (prev.assets > 0) {
                                          totalGrowth += ((current.assets - prev.assets) / prev.assets * 100);
                                          growthCount++;
                                        }
                                      }
                                    });
                                    
                                    return growthCount > 0 ? `${(totalGrowth / growthCount).toFixed(1)}%` : '0%';
                                  })()}
                                </div>
                              </div>
                              <div>
                                <Text type="secondary" style={{ fontSize: '12px' }}>Average Participant Growth</Text>
                                <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#764ba2' }}>
                                  {(() => {
                                    if (!selectedPlan || !selectedPlan.yearly_data) return '0%';
                                    const years = Object.keys(selectedPlan.yearly_data).sort();
                                    let totalGrowth = 0;
                                    let growthCount = 0;
                                    
                                    years.forEach((year, index) => {
                                      if (index > 0) {
                                        const current = selectedPlan.yearly_data[year];
                                        const prev = selectedPlan.yearly_data[years[index - 1]];
                                        const currentParticipants = current.participants_eoy || current.participants || 0;
                                        const prevParticipants = prev.participants_eoy || prev.participants || 0;
                                        if (prevParticipants > 0) {
                                          totalGrowth += ((currentParticipants - prevParticipants) / prevParticipants * 100);
                                          growthCount++;
                                        }
                                      }
                                    });
                                    
                                    return growthCount > 0 ? `${(totalGrowth / growthCount).toFixed(1)}%` : '0%';
                                  })()}
                                </div>
                              </div>
                            </div>
                          </Card>
                        </Space>
                      </Col>
                    </Row>
                  </div>
                )
              },
              {
                key: 'compliance',
                label: (
                  <span>
                    <SafetyCertificateOutlined /> Compliance & Documents
                  </span>
                ),
                children: (
                  <div>
                    <Row gutter={[24, 24]}>
                      <Col span={12}>
                        <Card title={<span><CheckCircleOutlined /> Compliance History</span>} style={{ borderRadius: '8px' }}>
                          <Timeline>
                            {Object.entries(selectedPlan.compliance_flags_by_year).reverse().map(([year, flag]) => (
                              <Timeline.Item 
                                key={year}
                                color={flag === 0 ? 'green' : 'red'}
                                dot={flag === 0 ? <CheckCircleOutlined /> : <ExclamationCircleOutlined />}
                              >
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <Text strong>{year}</Text>
                                  <Tag color={flag === 0 ? 'success' : 'error'}>
                                    {flag === 0 ? 'Compliant' : 'Issues Found'}
                                  </Tag>
                                </div>
                              </Timeline.Item>
                            ))}
                          </Timeline>
                        </Card>
                      </Col>
                      
                      <Col span={12}>
                        <Card title={<span><FileTextOutlined /> Form 5500 Documents</span>} style={{ borderRadius: '8px' }}>
                          <List
                            dataSource={Object.entries(selectedPlan.form_5500_links_by_year).reverse()}
                            renderItem={([year, link]) => (
                              <List.Item>
                                <List.Item.Meta
                                  avatar={<Avatar icon={<FileTextOutlined />} style={{ backgroundColor: '#667eea' }} />}
                                  title={<a href={link} target="_blank" rel="noopener noreferrer">{year} Form 5500</a>}
                                  description="Click to view official Form 5500 filing"
                                />
                                <Button 
                                  type="link" 
                                  icon={<LinkOutlined />}
                                  href={link}
                                  target="_blank"
                                >
                                  Open
                                </Button>
                              </List.Item>
                            )}
                          />
                        </Card>
                      </Col>
                    </Row>
                  </div>
                )
              },
              {
                key: 'analytics',
                label: (
                  <span>
                    <PieChartOutlined /> Analytics & Insights
                  </span>
                ),
                children: (
                  <div>
                    <Row gutter={[24, 24]}>
                      <Col span={8}>
                        <Card title="Year-over-Year Growth" style={{ borderRadius: '8px' }}>
                          <ResponsiveContainer width="100%" height={250}>
                            <RadarChart data={years.slice(-5).map(year => ({
                              year,
                              assets: selectedPlan.growth_rate_by_year?.[year] || 0,
                              participants: ((selectedPlan.yearly_data[year].participants_eoy - selectedPlan.yearly_data[years[years.indexOf(year)-1]]?.participants_eoy || 0) / (selectedPlan.yearly_data[years[years.indexOf(year)-1]]?.participants_eoy || 1) * 100).toFixed(1)
                            }))}>
                              <PolarGrid />
                              <PolarAngleAxis dataKey="year" />
                              <PolarRadiusAxis angle={90} domain={[0, 40]} />
                              <Radar name="Asset Growth %" dataKey="assets" stroke="#667eea" fill="#667eea" fillOpacity={0.6} />
                              <Radar name="Participant Growth %" dataKey="participants" stroke="#764ba2" fill="#764ba2" fillOpacity={0.6} />
                              <Legend />
                            </RadarChart>
                          </ResponsiveContainer>
                        </Card>
                      </Col>
                      
                      <Col span={8}>
                        <Card title="Financial Breakdown" style={{ borderRadius: '8px' }}>
                          <Space direction="vertical" size="large" style={{ width: '100%' }}>
                            <div>
                              <Text type="secondary">Assets per Participant</Text>
                              <Title level={3}>${(latestData.assets / latestData.participants_eoy).toFixed(0)}</Title>
                              <Progress percent={80} strokeColor="#667eea" showInfo={false} />
                            </div>
                            <Divider />
                            <div>
                              <Text type="secondary">Beginning vs End of Year Assets</Text>
                              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '8px' }}>
                                <Statistic 
                                  title="BOY" 
                                  value={latestData.assets_boy} 
                                  formatter={(value) => `$${(value / 1000000).toFixed(1)}M`}
                                  valueStyle={{ fontSize: '20px' }}
                                />
                                <ArrowUpOutlined style={{ fontSize: '24px', color: '#52c41a', margin: 'auto 0' }} />
                                <Statistic 
                                  title="EOY" 
                                  value={latestData.assets} 
                                  formatter={(value) => `$${(value / 1000000).toFixed(1)}M`}
                                  valueStyle={{ fontSize: '20px', color: '#52c41a' }}
                                />
                              </div>
                            </div>
                          </Space>
                        </Card>
                      </Col>
                      
                      <Col span={8}>
                        <Card title="Key Insights" style={{ borderRadius: '8px' }}>
                          <Alert
                            message="Growth Trajectory"
                            description={`This plan has shown ${selectedPlan.estimated_growth_rate}% estimated annual growth with ${complianceRate}% compliance rate.`}
                            type={selectedPlan.estimated_growth_rate > 10 ? "success" : "info"}
                            showIcon
                            style={{ marginBottom: '16px' }}
                          />
                          <Alert
                            message="Risk Assessment"
                            description={`${(selectedPlan.odds_of_zero_assets * 100).toFixed(0)}% risk of zero assets. ${selectedPlan.odds_of_zero_assets < 0.2 ? 'Low risk profile.' : 'Moderate to high risk - monitor closely.'}`}
                            type={selectedPlan.odds_of_zero_assets < 0.2 ? "success" : "warning"}
                            showIcon
                          />
                        </Card>
                      </Col>
                    </Row>
                    
                    {/* Additional yearly data table */}
                    <Card title="Complete Historical Data" style={{ marginTop: '24px', borderRadius: '8px' }}>
                      <Table
                        dataSource={years.map(year => ({
                          year,
                          ...selectedPlan.yearly_data[year],
                          growth_rate: selectedPlan.growth_rate_by_year?.[year] || 0,
                          compliance: selectedPlan.compliance_flags_by_year[year],
                          form_link: selectedPlan.form_5500_links_by_year[year]
                        }))}
                        columns={[
                          { title: 'Year', dataIndex: 'year', key: 'year' },
                          { 
                            title: 'Assets (EOY)', 
                            dataIndex: 'assets', 
                            key: 'assets',
                            render: (value) => formatCurrency(value)
                          },
                          { 
                            title: 'Assets (BOY)', 
                            dataIndex: 'assets_boy', 
                            key: 'assets_boy',
                            render: (value) => formatCurrency(value)
                          },
                          { title: 'Participants', dataIndex: 'participants_eoy', key: 'participants_eoy' },
                          { 
                            title: 'Growth Rate', 
                            dataIndex: 'growth_rate', 
                            key: 'growth_rate',
                            render: (value) => <Tag color={value > 0 ? 'green' : 'red'}>{value}%</Tag>
                          },
                          { 
                            title: 'Compliance', 
                            dataIndex: 'compliance', 
                            key: 'compliance',
                            render: (value) => value === 0 ? 
                              <Tag color="success" icon={<CheckCircleOutlined />}>Compliant</Tag> : 
                              <Tag color="error" icon={<ExclamationCircleOutlined />}>Issues</Tag>
                          },
                          { 
                            title: 'Form 5500', 
                            dataIndex: 'form_link', 
                            key: 'form_link',
                            render: (link) => <a href={link} target="_blank" rel="noopener noreferrer"><LinkOutlined /></a>
                          }
                        ]}
                        pagination={false}
                        size="small"
                      />
                    </Card>
                  </div>
                )
              }
            ]}
          />
        </div>
      </div>
    );
  };

  const activeFilterCount = Object.entries(filters).reduce((count, [key, filter]) => {
    if (Array.isArray(filter)) {
      return count + (filter.length > 0 ? 1 : 0);
    } else if (typeof filter === 'string') {
      return count + (filter.length > 0 ? 1 : 0);
    } else if (key === 'assetRange' || key === 'participantRange' || key === 'estimatedGrowthRange' || key === 'oddsRange' || key === 'dppRange') {
      // Check if range is different from default
      const defaults = {
        assetRange: [0, 10000000],
        participantRange: [0, 500],
        estimatedGrowthRange: [0, 50],
        oddsRange: [0, 100],
        dppRange: [0, 200000]
      };
      return count + (JSON.stringify(filter) !== JSON.stringify(defaults[key]) ? 1 : 0);
    }
    return count;
  }, 0);

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: '#6366f1',
          borderRadius: 8,
        },
      }}
    >
      <PageContainer
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        {/* Header Section */}

        {/* Search Section */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.3 }}
        >
          <SearchSection>
            <Space direction="vertical" size={16} style={{ width: '100%', padding: '0px !important' }}>
              <Input
                size="large"
                placeholder="Search by plan name, sponsor name, city, or EIN..."
                prefix={<SearchOutlined />}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ borderRadius: 8 }}
              />
              
              <Space>
                <Button
                  icon={<FilterOutlined />}
                  onClick={() => setShowFilters(!showFilters)}
                  type={showFilters ? 'primary' : 'default'}
                >
                  Filters {activeFilterCount > 0 && `(${activeFilterCount})`}
                </Button>
                
                {activeFilterCount > 0 && (
                  <Space wrap>
                    {filters.state.length > 0 && filters.state.map(state => (
                      <FilterTag 
                        key={state} 
                        closable 
                        onClose={() => setFilters(prev => ({ ...prev, state: prev.state.filter(s => s !== state) }))}
                      >
                        State: {state}
                      </FilterTag>
                    ))}
                    {filters.city.length > 0 && filters.city.map(city => (
                      <FilterTag 
                        key={city} 
                        closable 
                        onClose={() => setFilters(prev => ({ ...prev, city: prev.city.filter(c => c !== city) }))}
                      >
                        City: {city}
                      </FilterTag>
                    ))}
                    {filters.planCodes.length > 0 && filters.planCodes.map(code => (
                      <FilterTag 
                        key={code} 
                        closable 
                        onClose={() => setFilters(prev => ({ ...prev, planCodes: prev.planCodes.filter(c => c !== code) }))}
                      >
                        Code: {code}
                      </FilterTag>
                    ))}
                    {filters.planType.length > 0 && filters.planType.map(type => (
                      <FilterTag 
                        key={type} 
                        closable 
                        onClose={() => setFilters(prev => ({ ...prev, planType: prev.planType.filter(t => t !== type) }))}
                      >
                        Type: {type}
                      </FilterTag>
                    ))}
                    {filters.industry.length > 0 && filters.industry.map(industry => (
                      <FilterTag 
                        key={industry} 
                        closable 
                        onClose={() => setFilters(prev => ({ ...prev, industry: prev.industry.filter(i => i !== industry) }))}
                      >
                        Industry: {industry}
                      </FilterTag>
                    ))}
                    {filters.healthScore.length > 0 && filters.healthScore.map(score => (
                      <FilterTag 
                        key={score} 
                        closable 
                        onClose={() => setFilters(prev => ({ ...prev, healthScore: prev.healthScore.filter(s => s !== score) }))}
                      >
                        Health: {score}
                      </FilterTag>
                    ))}
                    {filters.wealthScore.length > 0 && filters.wealthScore.map(score => (
                      <FilterTag 
                        key={score} 
                        closable 
                        onClose={() => setFilters(prev => ({ ...prev, wealthScore: prev.wealthScore.filter(s => s !== score) }))}
                      >
                        Wealth: {score}
                      </FilterTag>
                    ))}
                    {filters.ein && (
                      <FilterTag 
                        closable 
                        onClose={() => setFilters(prev => ({ ...prev, ein: '' }))}
                      >
                        EIN: {filters.ein}
                      </FilterTag>
                    )}
                    {filters.pn && (
                      <FilterTag 
                        closable 
                        onClose={() => setFilters(prev => ({ ...prev, pn: '' }))}
                      >
                        PN: {filters.pn}
                      </FilterTag>
                    )}
                    {activeFilterCount > 0 && (
                      <Button 
                        type="link" 
                        size="small" 
                        onClick={() => setFilters({
                          state: [],
                          city: [],
                          ein: '',
                          pn: '',
                          assetRange: [0, 10000000],
                          participantRange: [0, 500],
                          estimatedGrowthRange: [0, 50],
                          oddsRange: [0, 100],
                          dppRange: [0, 200000],
                          planCodes: [],
                          planType: [],
                          healthScore: [],
                          wealthScore: [],
                          industry: []
                        })}
                      >
                        Clear all
                      </Button>
                    )}
                  </Space>
                )}
              </Space>
            </Space>
          </SearchSection>
        </motion.div>

        {/* Filters Section */}
        <AnimatePresence>
          {showFilters && (
            <FiltersSection
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={12} md={6}>
                  <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>State</label>
                  <Select
                    mode="multiple"
                    placeholder="Select states"
                    style={{ width: '100%' }}
                    value={filters.state}
                    onChange={(value) => setFilters(prev => ({ ...prev, state: value }))}
                  >
                    {[...new Set(data.map(item => item.state))].map(state => (
                      <Option key={state} value={state}>{state}</Option>
                    ))}
                  </Select>
                </Col>
                
                <Col xs={24} sm={12} md={6}>
                  <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>City</label>
                  <Select
                    mode="multiple"
                    placeholder="Select cities"
                    style={{ width: '100%' }}
                    value={filters.city}
                    onChange={(value) => setFilters(prev => ({ ...prev, city: value }))}
                  >
                    {[...new Set(data.map(item => item.city))].map(city => (
                      <Option key={city} value={city}>{city}</Option>
                    ))}
                  </Select>
                </Col>
                
                <Col xs={24} sm={12} md={6}>
                  <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>Plan Type</label>
                  <Select
                    mode="multiple"
                    placeholder="Select plan types"
                    style={{ width: '100%' }}
                    value={filters.planType}
                    onChange={(value) => setFilters(prev => ({ ...prev, planType: value }))}
                  >
                    {[...new Set(data.map(item => item.plan_type))].map(type => (
                      <Option key={type} value={type}>{type}</Option>
                    ))}
                  </Select>
                </Col>
                
                <Col xs={24} sm={12} md={6}>
                  <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>Industry</label>
                  <Select
                    mode="multiple"
                    placeholder="Select industries"
                    style={{ width: '100%' }}
                    value={filters.industry}
                    onChange={(value) => setFilters(prev => ({ ...prev, industry: value }))}
                  >
                    {[...new Set(data.map(item => item.plan_sponsor_industry))].map(ind => (
                      <Option key={ind} value={ind}>{ind}</Option>
                    ))}
                  </Select>
                </Col>
              </Row>
              
              <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
                <Col xs={24} sm={12} md={6}>
                  <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>Plan Codes</label>
                  <Select
                    mode="multiple"
                    placeholder="Select plan codes"
                    style={{ width: '100%' }}
                    value={filters.planCodes}
                    onChange={(value) => setFilters(prev => ({ ...prev, planCodes: value }))}
                  >
                    {Object.entries(PLAN_CODES_MAP).map(([code, description]) => (
                      <Option key={code} value={code}>
                        <Tooltip title={description}>{code}</Tooltip>
                      </Option>
                    ))}
                  </Select>
                </Col>
                
                <Col xs={24} sm={12} md={6}>
                  <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>Health Score</label>
                  <Select
                    mode="multiple"
                    placeholder="Select health scores"
                    style={{ width: '100%' }}
                    value={filters.healthScore}
                    onChange={(value) => setFilters(prev => ({ ...prev, healthScore: value }))}
                  >
                    <Option value="A">A</Option>
                    <Option value="B+">B+</Option>
                    <Option value="B">B</Option>
                    <Option value="C">C</Option>
                  </Select>
                </Col>
                
                <Col xs={24} sm={12} md={6}>
                  <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>Wealth Score</label>
                  <Select
                    mode="multiple"
                    placeholder="Select wealth scores"
                    style={{ width: '100%' }}
                    value={filters.wealthScore}
                    onChange={(value) => setFilters(prev => ({ ...prev, wealthScore: value }))}
                  >
                    <Option value="High">High</Option>
                    <Option value="Medium">Medium</Option>
                    <Option value="Low">Low</Option>
                  </Select>
                </Col>
                
                <Col xs={24} sm={12} md={6}>
                  <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>EIN</label>
                  <Input
                    placeholder="Enter EIN"
                    value={filters.ein}
                    onChange={(e) => setFilters(prev => ({ ...prev, ein: e.target.value }))}
                  />
                </Col>
                
                <Col xs={24} sm={12} md={6}>
                  <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>Plan Number</label>
                  <Input
                    placeholder="Enter PN"
                    value={filters.pn}
                    onChange={(e) => setFilters(prev => ({ ...prev, pn: e.target.value }))}
                  />
                </Col>
              </Row>
              
              <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
                <Col xs={24} sm={24} md={8}>
                  <RangeInputFilter
                    label="Asset Range"
                    range={filters.assetRange}
                    onChange={(value) => setFilters(prev => ({ ...prev, assetRange: value }))}
                    min={0}
                    max={10000000}
                    step={100000}
                    formatter={(value) => `$${(value / 1000).toFixed(0)}K`}
                    parser={(value) => parseFloat(value.replace(/\$|K/g, '')) * 1000}
                  />
                </Col>
                
                <Col xs={24} sm={24} md={8}>
                  <RangeInputFilter
                    label="Participant Range"
                    range={filters.participantRange}
                    onChange={(value) => setFilters(prev => ({ ...prev, participantRange: value }))}
                    min={0}
                    max={500}
                    step={1}
                    formatter={(value) => value?.toString()}
                    parser={(value) => parseInt(value)}
                  />
                </Col>
                
                <Col xs={24} sm={24} md={8}>
                  <RangeInputFilter
                    label="Est. Growth Rate"
                    range={filters.estimatedGrowthRange}
                    onChange={(value) => setFilters(prev => ({ ...prev, estimatedGrowthRange: value }))}
                    min={0}
                    max={50}
                    step={0.5}
                    formatter={(value) => `${value}%`}
                    parser={(value) => value.replace('%', '')}
                    suffix="%"
                  />
                </Col>
              </Row>
              
              <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
                <Col xs={24} sm={24} md={12}>
                  <RangeInputFilter
                    label="Odds of Zero Assets"
                    range={filters.oddsRange}
                    onChange={(value) => setFilters(prev => ({ ...prev, oddsRange: value }))}
                    min={0}
                    max={100}
                    step={1}
                    formatter={(value) => `${value}%`}
                    parser={(value) => value.replace('%', '')}
                    suffix="%"
                  />
                </Col>
                
                <Col xs={24} sm={24} md={12}>
                  <RangeInputFilter
                    label="Dollars per Participant"
                    range={filters.dppRange}
                    onChange={(value) => setFilters(prev => ({ ...prev, dppRange: value }))}
                    min={0}
                    max={200000}
                    step={1000}
                    formatter={(value) => `$${value.toLocaleString()}`}
                    parser={(value) => value.replace(/\$|,/g, '')}
                  />
                </Col>
              </Row>
            </FiltersSection>
          )}
        </AnimatePresence>

        {/* Statistics Cards */}
        {statistics && (
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col xs={24} sm={12} md={6}>
                <StatsCard gradient="#1e3a8a 0%, #3b82f6 100%">
                  <div>
                    <Text style={{ color: 'white', opacity: 0.8 }}>Plans Found</Text>
                    <Title level={2} style={{ color: 'white', margin: '8px 0' }}>
                      {statistics.totalPlans.toLocaleString()}
                    </Title>
                  </div>
                </StatsCard>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <StatsCard gradient="#0ea5e9 0%, #0284c7 100%">
                  <div>
                    <Text style={{ color: 'white', opacity: 0.8 }}>Total Assets</Text>
                    <Title level={2} style={{ color: 'white', margin: '8px 0' }}>
                      ${(statistics.totalAssets / 1000000).toFixed(1)}M
                    </Title>
                  </div>
                </StatsCard>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <StatsCard gradient="#06b6d4 0%, #0891b2 100%">
                  <div>
                    <Text style={{ color: 'white', opacity: 0.8 }}>Total Participants</Text>
                    <Title level={2} style={{ color: 'white', margin: '8px 0' }}>
                      {statistics.totalParticipants.toLocaleString()}
                    </Title>
                  </div>
                </StatsCard>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <StatsCard gradient="#2563eb 0%, #1d4ed8 100%">
                  <div>
                    <Text style={{ color: 'white', opacity: 0.8 }}>Avg Growth Rate</Text>
                    <Title level={2} style={{ color: 'white', margin: '8px 0' }}>
                      {statistics.avgGrowthRate.toFixed(1)}%
                    </Title>
                  </div>
                </StatsCard>
              </Col>
            </Row>
          </motion.div>
        )}

        {/* Results Table */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.4 }}
          style={{ flex: 1, display: 'flex', overflow: 'hidden' }}
        >
          <GlassCard>
            <ContentWrapper>
              <TableWrapper>
                <StyledTable
                  columns={columns}
                  dataSource={filteredData}
                  rowKey={(record) => `${record.ein}-${record.pn}`}
                  loading={loading}
                  pagination={{
                    pageSize: 10,
                    showSizeChanger: true,
                    showTotal: (total) => `Total ${total} plans`,
                  }}
                  onRow={(record) => ({
                    onClick: () => handleRowClick(record),
                    style: { cursor: 'pointer' }
                  })}
                />
              </TableWrapper>
            </ContentWrapper>
          </GlassCard>
        </motion.div>

        {/* Plan Details Modal */}
        <Modal
          title={null}
          open={modalVisible}
          onCancel={() => setModalVisible(false)}
          footer={null}
          width="95vw"
          style={{ top: 20 }}
          bodyStyle={{ padding: 0, overflowY: 'auto' }}
          styles={{
            content: { padding: 0 },
            body: { padding: 0 },
          }}
          closeIcon={<CloseOutlined style={{ fontSize: '20px' }} />}
        >
          {renderPlanDetails()}
        </Modal>
      </PageContainer>
    </ConfigProvider>
  );
};

export default PlanSearchAutomation; 